"use client";

import UnlockedIcon from "@/assets/unlocked.svg";
import Card from "@/components/Card";
import {
  CLIFF_DURATION_DAYS,
  VESTING_DURATION_DAYS,
  VESTING_START_DATE,
} from "@/config";
import { getCliffTimeRemaining, getVestingTimeRemaining } from "@/utils";
import ClaimClosedNotice from "../ClaimClosedNotice";
import SecondaryButton from "../SecondaryButton";
import UnlockCountdown from "../UnlockCountdown";
import Tooltip from "../Tooltip";

interface IProps {
  claimedSpon: number;
  unlockedSpon: number;
  remainingSpon: number;
  showModal: () => void;
  airdropPercentage: number;
  airdropSponAmount: number;
  isClaimClosed?: boolean;
  claimDeadline?: number | null;
}

const formatAmount = (amount: string | number, truncate: boolean = true) => {
  const formattedAmount =
    typeof amount === "string" ? Number(amount) / 10 ** 18 : amount / 10 ** 18;

  return truncate ? Number(formattedAmount.toFixed(6)) : formattedAmount;
};

const VestingDetailsCard = ({
  claimedSpon,
  unlockedSpon,
  remainingSpon,
  showModal,
  // airdropPercentage,
  airdropSponAmount,
  isClaimClosed = false,
  claimDeadline = null,
}: IProps) => {
  const isVesting = new Date().getTime() >= VESTING_START_DATE;
  const vestingTimeRemaining = getVestingTimeRemaining();
  const cliffTimeRemaining = getCliffTimeRemaining();

  const items = [];

  if (isVesting)
    items.push({
      id: 1,
      label: "Days Remaining:",
      value: `${vestingTimeRemaining.days + 1} of ${VESTING_DURATION_DAYS}`, // TODO: update later
    });
  else
    items.push({
      id: 1,
      label: "Days to Unlock:",
      value: `${
        CLIFF_DURATION_DAYS - cliffTimeRemaining.days - 1 // TODO: update later
      } of ${CLIFF_DURATION_DAYS}`,
    });

  items.push(
    {
      id: 2,
      label: "Unlocked So Far:",
      value: (
        <Tooltip
          position="bottom"
          tooltipContent={formatAmount(unlockedSpon, false) + " SPON"}
        >
          {isVesting ? formatAmount(unlockedSpon) : 0} SPON
        </Tooltip>
      ),
    },
    {
      id: 3,
      label: "Claimed",
      value: (
        <Tooltip
          position="bottom"
          tooltipContent={formatAmount(claimedSpon, false) + " SPON"}
        >
          {isVesting ? formatAmount(claimedSpon) : 0} SPON
        </Tooltip>
      ),
    },
    {
      id: 4,
      label: "Remaining",
      value: (
        <Tooltip
          position="bottom"
          tooltipContent={formatAmount(remainingSpon, false) + " SPON"}
        >
          {formatAmount(remainingSpon)} SPON
        </Tooltip>
      ),
    }
  );

  const vestingUnlockedPercentage = isVesting
    ? ((claimedSpon + unlockedSpon) * 100) /
      (remainingSpon + claimedSpon + unlockedSpon)
    : 0;
  const cliffUnlockPercentage = cliffTimeRemaining.percentage;

  const vestingProgressTopLabels = [
    {
      id: 1,
      classNames: "left-0",
      content: (
        <>
          Day {CLIFF_DURATION_DAYS}{" "}
          <span className="hidden md:inline">(Unlocking Starts)</span>
        </>
      ),
    },
    {
      id: 2,
      classNames: "left-1/2 hidden md:inline",
      content: `Day ${
        CLIFF_DURATION_DAYS + VESTING_DURATION_DAYS / 2
      } (50% Unlocked)`,
    },
    {
      id: 3,
      classNames: "right-0",
      content: (
        <>
          Day {CLIFF_DURATION_DAYS + VESTING_DURATION_DAYS}{" "}
          <span className="hidden md:inline">(Fully Unlocked)</span>
        </>
      ),
    },
  ];

  const vestingProgressBottomLabels = [
    {
      id: 1,
      classNames: "left-0",
      content: `${Number(airdropSponAmount.toFixed(2))} SPON`,
    },
    {
      id: 2,
      classNames: "left-1/2 hidden md:block",
      content: `${Number(
        (airdropSponAmount + formatAmount(remainingSpon) / 2).toFixed(1)
      )} SPON`,
    },
    {
      id: 3,
      classNames: "right-0",
      content: `${Number(
        (airdropSponAmount + formatAmount(remainingSpon)).toFixed(1)
      )} SPON`,
    },
  ];

  return (
    <>
      <Card className="!px-4 md:!px-8 py-9 flex-col gap-8 !bg-[#141416]">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-4 items-center">
            <div className="flex gap-3 font-albert-sans text-2xl leading-7 uppercase">
              {isVesting ? <UnlockedIcon /> : null}{" "}
              {isVesting ? (
                "VESTING DETAILS"
              ) : (
                <div>
                  UNLOCKS IN ⏳ <UnlockCountdown />
                </div>
              )}
            </div>
            <p className="text-[#9D9D9D] text-[13px] leading-5 font-gilroy-regular">
              Your tokens unlock in 2 months and can be claimed gradually over
              the next 6 months.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 justify-center">
            <div className="flex flex-col gap-2 px-6 py-5 bg-[#36B37E4D]">
              <span className="font-gilroy-regular text-[13px]">
                Unlocked Now
              </span>
              <span className="font-gilroy-bold text-lg">
                <Tooltip
                  position="bottom"
                  tooltipContent={formatAmount(unlockedSpon, false) + " SPON"}
                >
                  {isVesting ? formatAmount(unlockedSpon) : 0} SPON
                </Tooltip>
              </span>
            </div>
            <div className="flex flex-col gap-2 px-6 py-5 bg-white/5">
              <span className="font-gilroy-regular text-[13px]">
                Locked Rewards
              </span>
              <span className="font-gilroy-bold text-lg">
                <Tooltip
                  position="bottom"
                  tooltipContent={formatAmount(remainingSpon, false) + " SPON"}
                >
                  {formatAmount(remainingSpon)} SPON
                </Tooltip>
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-col p-6 gap-6 font-gilroy-regular text-white w-full bg-white/5">
          <div className="flex flex-col gap-y-5">
            <div className="flex justify-between text-base uppercase font-semibold tracking-widest">
              <span>{isVesting ? "Unlocking Progress" : "Progress"}</span>
              {isVesting ? (
                <span>{vestingUnlockedPercentage.toFixed(2)}%</span>
              ) : null}
            </div>
            <div className="flex py-4">
              <div className="bg-[#D9D9D9] relative bg-opacity-20 h-2 w-1/4">
                <div
                  style={{
                    width: cliffUnlockPercentage + "%",
                  }}
                  className="bg-white h-2"
                ></div>

                <span className="text-xs absolute translate-y-1.5 left-0">
                  {Number(airdropSponAmount.toFixed(2))} SPON
                </span>
                <span className="text-xs absolute -top-full left-0 -translate-y-3">
                  Day 1
                </span>
              </div>
              <div className="bg-[#D9D9D9] relative bg-opacity-20 h-2 w-3/4">
                <div className="h-[200%] -top-1 w-[1px] bg-white absolute left-1/2 hidden md:block"></div>
                <div className="h-[200%] -top-1 w-[1px] bg-white absolute right-0"></div>
                <div className="h-[200%] -top-1 w-[1px] bg-white absolute left-0"></div>
                <div
                  style={{
                    width: isVesting ? vestingUnlockedPercentage + "%" : "1px",
                  }}
                  className="bg-white h-2"
                ></div>
                {vestingProgressBottomLabels.map((item) => (
                  <span
                    key={item.id}
                    className={`text-xs absolute translate-y-1.5 ${item.classNames}`}
                  >
                    {item.content}
                  </span>
                ))}
                {vestingProgressTopLabels.map((item) => (
                  <span
                    key={item.id}
                    className={`text-xs absolute -top-full -translate-y-3 ${item.classNames}`}
                  >
                    {item.content}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 text-sm gap-x-20 gap-y-6">
            {items.map((item) => {
              return (
                <div key={item.id} className="flex justify-between leading-6">
                  <span>{item.label}</span>
                  <span className="text-[17px] font-jetbrans-mono leading-5 font-bold">
                    {item.value}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        {isClaimClosed ? <ClaimClosedNotice deadline={claimDeadline} /> : null}
        <SecondaryButton
          disabled={unlockedSpon <= 0 || isClaimClosed}
          variant="light"
          onClick={() => showModal()}
        >
          {isClaimClosed ? (
            "Claim Period Ended"
          ) : (
            <>
              Claim Available{" "}
              <span className="font-bold">
                {formatAmount(unlockedSpon)} SPON
              </span>
            </>
          )}
        </SecondaryButton>
      </Card>
    </>
  );
};

export default VestingDetailsCard;
